import dayjs, {Dayjs} from 'dayjs';
import {memoize} from 'lodash';

import {projectTz} from 'shared/helpers/dates';
import {TaskObjectType} from 'shared/models/task/const';
import {TaskModelRawDTO} from 'shared/models/task/task';
import {TaskDependencyDto} from 'shared/models/TaskDependency';

import {ChangeSet, ProjectCalendar} from './types';

const STATUS_UNCHANGED = 'unchanged';
const STATUS_CHANGED = 'changed';

export type Task = {
  id: string;
  object_type: TaskObjectType;
  actual_start: Dayjs | null;
  start_date: Dayjs;
  end_date: Dayjs;
  date_list: Dayjs[];
  duration: number;
};

export function addWorkdays(calendar: ProjectCalendar, dt: Dayjs, days: number): Dayjs {
  if (days > 0) {
    for (let i = 0; i < days; i++) {
      dt = nextWorkday(calendar, dt, 1);
    }
    return dt;
  } else if (days < 0) {
    for (let i = 0; i > days; i--) {
      dt = nextWorkday(calendar, dt, -1);
    }
    return dt;
  }
  return dt;
}

// function taskToStr(task) {
//   return JSON.stringify({
//     id: task.id,
//     start_date: task.start_date.format('YYYY-MM-DD'),
//     end_date: task.end_date.format('YYYY-MM-DD'),
//     duration: task.duration,
//     date_list: task.date_list.map((dt) => dt.format('YYYY-MM-DD')),
//   });
// }

function deepEqual(a, b) {
  if (a instanceof Array && b instanceof Array) {
    if (a.length != b.length) {
      return false;
    }
    for (let i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) {
        return false;
      }
    }
    return true;
  }
  return a == b;
}

function isoFormat(dt: Dayjs) {
  const mon = (dt.month() + 1).toString().padStart(2, '0');
  const day = dt.date().toString().padStart(2, '0');
  return `${dt.year()}-${mon}-${day}`;
}

function isWorkday(calendar: ProjectCalendar, dt: Dayjs): boolean {
  const isodate = isoFormat(dt);

  if (calendar.exceptions[isodate] !== undefined) {
    return calendar.exceptions[isodate];
  }

  if (calendar.work_days.has(dt.day())) {
    return true;
  }

  return false;
}

function nextWorkday(calendar: ProjectCalendar, dt: Dayjs, incr: number): Dayjs {
  while (true) {
    dt = dt.add(incr, 'day');
    if (isWorkday(calendar, dt)) {
      return dt;
    }
  }
}

export class TaskGraph {
  private calendar: ProjectCalendar;
  public taskMap: {[key: string]: Task};
  private deps: TaskDependencyDto[];
  private updates: {[key: string]: Partial<Task>};
  private predMap: {[key: string]: TaskDependencyDto[]};
  private succMap: {[key: string]: TaskDependencyDto[]};

  constructor(calendar: ProjectCalendar, tasks: Partial<TaskModelRawDTO>[], deps: TaskDependencyDto[]) {
    this.calendar = calendar;

    // implement caching here
    const parseDate = memoize((dtString: string | undefined) => {
      return dtString ? dayjs.tz(dtString) : null;
    });

    // Deep copy tasks and deps because they can be altered during reflow
    // TODO make sure we're not leaking observation into this
    this.taskMap = tasks.reduce((acc, taskdto) => {
      acc[taskdto.id] = {
        id: taskdto.id,
        object_type: taskdto.object_type,
        actual_start: parseDate(taskdto.actual_start),
        start_date: parseDate(taskdto.start_date),
        end_date: parseDate(taskdto.end_date),
        date_list: taskdto.date_list.map((dtString) => parseDate(dtString)) || [],
        duration: taskdto.duration,
      };
      return acc;
    }, {} as {[key: string]: Task});
    this.deps = deps.map((d) => ({...d}));
    this.updates = {};
    this.predMap = {};
    this.succMap = {};

    for (const dep of this.deps) {
      if (!this.predMap[dep.predTaskId]) {
        this.predMap[dep.predTaskId] = [];
      }
      if (!this.succMap[dep.taskId]) {
        this.succMap[dep.taskId] = [];
      }
      this.predMap[dep.predTaskId].push(dep);
      this.succMap[dep.taskId].push(dep);
    }
  }

  private getPredecessorTaskIds(taskId: string): string[] {
    const predTaskIds: string[] = [];
    for (const dep of this.succMap[taskId] || []) {
      predTaskIds.push(dep.depTaskId);
    }
    return predTaskIds;
  }

  public apply(changeSet: ChangeSet) {
    for (const [taskId, value] of Object.entries(changeSet)) {
      const task = this.taskMap[taskId];
      let dates = {};
      if (value.date_list) {
        dates = this.datesFromDateList(value.date_list.map((d) => projectTz(d)));
      } else if (value.start_date && value.duration) {
        dates = this.datesFromStartDuration(projectTz(value.start_date), value.duration);
      } else if (value.end_date && value.duration) {
        dates = this.datesFromEndDuration(projectTz(value.end_date), value.duration);
      } else if (value.start_date && value.end_date) {
        dates = this.datesFromStartEnd(projectTz(value.start_date), projectTz(value.end_date));
      }
      this.updates[taskId] = dates;
      Object.assign(task, dates);
      this.reflow(taskId);
    }

    return this.toRawDTO(this.updates);
  }

  private toRawDTO(updates: {[key: string]: Partial<Task>}): ChangeSet {
    const results = {};
    for (const [taskId, update] of Object.entries(updates)) {
      results[taskId] = {
        start_date: update.start_date.format('YYYY-MM-DD'),
        duration: update.duration,
        end_date: update.end_date.format('YYYY-MM-DD'),
        date_list: update.date_list.map((d) => d.format('YYYY-MM-DD')),
      };
    }
    return results;
  }

  public reflow(rootTaskId: string) {
    const succStatus: {[key: string]: string} = {};
    for (const taskId of this.topologicalSort()) {
      const task = this.taskMap[taskId];

      // If all predecessors are unchanged, task doesn't need to be reflowed
      // Special case any node downstream of root node to force recalculation
      const predTaskIds = this.getPredecessorTaskIds(taskId);
      const predsUnchanged = predTaskIds.every((predTaskId) => {
        return predTaskId != rootTaskId && succStatus[predTaskId] === STATUS_UNCHANGED;
      });
      if (predsUnchanged) {
        succStatus[taskId] = STATUS_UNCHANGED;
        continue;
      }

      // If a task has an actual_start, it must not be moved.
      if (task.actual_start) {
        succStatus[taskId] = STATUS_UNCHANGED;
        continue;
      }

      // tasks are scheduled based on their predecessors, so iterate over
      // dependencies where task_id is the successor
      // In the case of multiple dependencies, pick most pessimistic date
      const succUpdate = this.getLatestSuccessor(this.succMap[taskId]);
      if (succUpdate !== null) {
        if (!deepEqual(succUpdate.date_list, task.date_list)) {
          succStatus[taskId] = STATUS_CHANGED;
          Object.assign(task, succUpdate);
          this.updates[task.id] = succUpdate;
        } else {
          succStatus[taskId] = 'unchanged';
        }
      } else {
        succStatus[taskId] = 'unchanged';
      }
    }
  }

  private getLatestSuccessor(deps: TaskDependencyDto[]): Task {
    const possibleUpdates = [];
    for (const dep of deps) {
      const pred = this.taskMap[dep['predTaskId']];
      const succ = this.taskMap[dep['taskId']];
      possibleUpdates.push(this.succFromPred(pred, succ, dep));
    }

    if (possibleUpdates.length) {
      possibleUpdates.sort((a, b) => {
        // sort by start date reversed so first element of the array is the most pessimistic
        return -1 * (a.start_date.isBefore(b.start_date) ? -1 : 1);
      });

      return possibleUpdates[0];
    }

    return null;
  }

  public succFromPred(pred: Task, succ: Task, dep: TaskDependencyDto): Task {
    return this.succFromPredAttr(pred.start_date, pred.end_date, succ, dep);
  }

  private succFromPredAttr(predStart: Dayjs, predFinish: Dayjs, succ: Task, dep: TaskDependencyDto): Task {
    if (dep.depType === 'finish_start') {
      // By definition, if succ is a milestone with a predecessor, it's never a start milestone
      // So FS should put milestone on same day as predecessor
      const offset = succ.object_type === TaskObjectType.milestone ? 0 : 1;
      const start = addWorkdays(this.calendar, predFinish, offset + dep.lagDays);
      return this.moveTaskStart(succ, start);
    } else if (dep.depType === 'finish_finish') {
      const finish = addWorkdays(this.calendar, predFinish, dep.lagDays);
      return this.moveTaskFinish(succ, finish);
    } else if (dep.depType === 'start_start') {
      const start = addWorkdays(this.calendar, predStart, dep.lagDays);
      return this.moveTaskStart(succ, start);
    } else if (dep.depType === 'start_finish') {
      // Milestones are normally the same day as predecssor start, so get offset = 0
      // FS pred for Tasks are inverted FS, so get -1 offset
      const offset = succ.object_type === TaskObjectType.milestone ? 0 : -1;
      const finish = addWorkdays(this.calendar, predStart, offset + dep.lagDays);
      return this.moveTaskFinish(succ, finish);
    } else {
      throw new Error('Unsupported dependency type');
    }
  }

  /**
   * Calculate a new set of work_days for Task selecting a new start date.
   *
   * Start date is always included as a work day, additional work_days
   * are included if the task calendar.is_working returns True.  Duration is
   * preserved.
   *
   * @param {Task} succ - task to reschedule
   * @param {Dayjs} startDate new start date
   * @return {Task}
   */
  private moveTaskStart(succ: Task, startDate: Dayjs): Task {
    if (succ.object_type === TaskObjectType.milestone) {
      return {
        ...succ,
        start_date: startDate,
        end_date: startDate,
        date_list: [startDate],
      };
    }

    const workDays = succ.date_list;
    // this is clumsy.  Maybe I should have a dirty flag on task?
    if (succ.start_date == startDate && workDays.length) {
      return succ;
    }

    return {
      ...succ,
      ...this.datesFromStartDuration(startDate, succ.duration),
    };
  }

  private datesFromEndDuration(endDate: Dayjs, duration: number): Partial<Task> {
    let dateIter = endDate;
    const nextWorkdays = [endDate];
    let dur = 1;

    // Schedule work days for each day of duration
    while (dur < duration) {
      dateIter = nextWorkday(this.calendar, dateIter, -1);
      nextWorkdays.unshift(dateIter);
      dur += 1;
    }

    return this.datesFromDateList(nextWorkdays);
  }

  private datesFromStartEnd(startDate: Dayjs, endDate: Dayjs): Partial<Task> {
    let dateIter = startDate;
    const nextWorkdays = [];

    // Schedule work days for days from start to finish.
    while (dateIter < endDate) {
      nextWorkdays.push(dateIter);
      dateIter = nextWorkday(this.calendar, dateIter, 1);
    }
    // Slightly different logic allows task to start or end on non-working days
    if (startDate !== endDate) {
      nextWorkdays.push(endDate);
    }

    return this.datesFromDateList(nextWorkdays);
  }

  private datesFromDateList(dateList: Dayjs[]): Partial<Task> {
    return {
      start_date: dateList[0],
      duration: dateList.length,
      end_date: dateList[dateList.length - 1],
      date_list: dateList,
    };
  }

  private datesFromStartDuration(startDate: Dayjs, duration: number): Partial<Task> {
    let dateIter = startDate;
    const nextWorkdays = [dateIter];
    let dur = 1;

    // Schedule work days for each day of duration
    while (dur < duration) {
      dateIter = nextWorkday(this.calendar, dateIter, 1);
      nextWorkdays.push(dateIter);
      dur += 1;
    }

    return this.datesFromDateList(nextWorkdays);
  }

  /**
   * Calculate a new set of work_days for Task selecting a new finish date.
   *
   * finish date is always included as a work day, additional work_days
   * are included if the task calendar.is_working returns True.  Duration is
   * preserved.
   *
   * @param {Task} task - Task to reschedule
   * @param {Dayjs} finish - new task end_date
   * @return {Task} Rescheduled task
   */
  private moveTaskFinish(task: Task, finish: Dayjs): Task {
    if (task.end_date === finish && task.date_list.length > 0) {
      return task;
    }

    let dtIter = finish;
    const days = [dtIter];
    let dur = 1;

    // Schedule work days for each duration (minus extra work which is
    // expected to happen on Saturdays or OT on other scheduled days)
    while (dur < task['duration']) {
      dtIter = nextWorkday(this.calendar, dtIter, -1);
      days.unshift(dtIter);
      dur += 1;
    }

    return {
      ...task,
      start_date: days[0],
      end_date: days[days.length - 1],
      date_list: days,
    };
  }

  public topologicalSort(): string[] {
    const inDegree = {};
    const topoSortIds = [];

    // Calculate in-degree of each node
    // for (const task of this.tasks) {
    for (const taskId of Object.keys(this.taskMap)) {
      inDegree[taskId] = 0;
    }
    for (const task of Object.values(this.taskMap)) {
      if (this.predMap[task.id]) {
        for (const dep of this.predMap[task.id]) {
          inDegree[dep.taskId]++;
        }
      }
    }

    // Initialize queue with nodes having 0 in-degree
    const queue = [];
    for (const taskId in inDegree) {
      if (inDegree[taskId] === 0) {
        queue.push(taskId);
      }
    }

    // Process nodes in topological order
    while (queue.length > 0) {
      const taskId = queue.shift();
      topoSortIds.push(taskId);

      if (this.predMap[taskId]) {
        for (const dep of this.predMap[taskId]) {
          inDegree[dep.taskId]--;
          if (inDegree[dep.taskId] === 0) {
            queue.push(dep.taskId);
          }
        }
      }
    }

    return topoSortIds;
  }
}
